"use client";
import * as _ from "lodash";
import { Sheet } from "@mui/joy";
import { memo, useEffect, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import { TextareaType } from "data/CellComponent";
import Quill from "./Quill";
import { usePrevious } from "@uidotdev/usehooks";
import PlainTextarea from "./PlainTextarea";
import { debounce } from "lodash";

function TextareaInput({
  taskKey,
  placeholder,
  initialHtmlString,
  initialValue,
  isCoachField,
  isCoach,
  index,
  readOnly,
  setData,
  lineIndex,
  cellIndex,
  editorKey,
  id,
}: {
  taskKey: string;
  placeholder?: string;
  initialHtmlString?: string;
  initialValue?: string;
  isCoachField?: boolean;
  isCoach?: boolean;
  index: number;
  readOnly?: boolean;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  editorKey?: string;
  id?: string;
}) {
  const [currentContent, setCurrentContent] = useState<{
    htmlString?: string;
    value?: string;
  }>({ htmlString: initialHtmlString, value: initialValue });
  const previousContent = usePrevious(currentContent);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    setCurrentContent({ value: initialValue, htmlString: initialHtmlString });
  }, [initialHtmlString, initialValue]);

  const handleChange = (value: string, htmlString?: string) => {
    setData((data) => {
      if (data && data[index].lines) {
        const originalCurrentContent = (data[index].lines[lineIndex][cellIndex]
          .content || {}) as TextareaType;
        const originalHtmlString = originalCurrentContent.htmlString;
        const originalValue = originalCurrentContent.value || "";

        if (!(originalHtmlString === htmlString && originalValue === value)) {
          const data_temp = _.cloneDeep(data);
          const currentContent =
            data_temp[index].lines[lineIndex][cellIndex].content || {};

          data_temp[index].lines[lineIndex][cellIndex].content = {
            ...currentContent,
            htmlString: (htmlString || "").length > 0 ? htmlString : undefined,
            value: isCoach ? value.replace(/[\n\r]+$/, "") : value,
          };

          return data_temp;
        }
        return data;
      }
      return data;
    });
  };

  const handleChangeTemp = (value: string, htmlString?: string) => {
    setCurrentContent({ value: value, htmlString: htmlString });
  };

  //0->1 1->0 바로 저장
  useEffect(() => {
    if (currentContent && (currentContent.value || "")?.length >= 0) {
      if (
        !previousContent ||
        ((previousContent?.value || "").trim().length === 0 &&
          (currentContent.value || "")?.trim().length > 0) ||
        (previousContent.value &&
          previousContent.value?.trim().length > 0 &&
          currentContent.value?.trim().length === 0)
      ) {
        // handleChange(currentContent.value || "", currentContent.htmlString);
      }
    }
  }, [currentContent]);

  // lodash의 debounce 함수를 사용하여 디바운스 함수를 생성합니다.
  const debouncedSaveText = debounce(handleChange, 500);

  useEffect(() => {
    // text 상태가 변경될 때마다 debouncedSaveText 함수를 호출합니다.
    debouncedSaveText(currentContent.value || "", currentContent.htmlString);

    // 컴포넌트가 언마운트될 때 디바운스 함수를 취소합니다.
    return () => debouncedSaveText.cancel();
  }, [currentContent, debouncedSaveText]);

  const isCoachFieldEmpty =
    isCoach && isCoachField && (initialValue || "").trim().length === 0;

  return (
    <div className={`w-full ${readOnly ? "pointer-events-none" : ""}`}>
      <Sheet
        sx={{
          position: "relative",
          height: "100%",
          backgroundColor:
            isCoach && isCoachField ? "background.level2" : "transparent",
          borderRadius: "4px",
          borderWidth: !isCoach && isCoachField ? 0 : 1,
          borderColor:
            isCoach && isCoachField
              ? "background.level2"
              : "primary.deactivated",
          px: !isCoach && isCoachField ? 0 : "12px",
          py: !isCoach && isCoachField ? 0 : "5px",
          color: "text.positive",
          outlineWidth: "10px",
          outlineColor: "black",
        }}
        className="outline-[10px] outline-black"
      >
        {isFocused && (
          <Sheet
            className="w-[calc(100%+2px)] h-[calc(100%+2px)] top-[-1px] left-[-1px]"
            sx={{
              backgroundColor: "transparent",
              position: "absolute",
              borderWidth: "var(--joy-focus-thickness, 2)",
              borderRadius: "4px",
              borderColor: "primary.solid",
            }}
          />
        )}
        {isCoachFieldEmpty && (
          <Sheet
            className="w-[calc(100%+2px)] h-[calc(100%+2px)] top-[-1px] left-[-1px]"
            sx={{
              backgroundColor: "transparent",
              position: "absolute",
              borderWidth: "var(--joy-focus-thickness, 2)",
              borderRadius: "4px",
              borderColor: "danger.outlinedColor",
            }}
          />
        )}

        {isCoach ? (
          <Quill
            taskKey={taskKey}
            placeholder={placeholder}
            initialHtmlString={initialHtmlString}
            initialValue={initialValue}
            handleChange={handleChangeTemp}
            index={index}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            editorKey={`${editorKey}`.substring(2)}
            disableInlineToolbar={!isCoach}
            readOnly={!isCoach && isCoachField}
          />
        ) : (
          <PlainTextarea
            taskKey={taskKey}
            placeholder={placeholder}
            initialValue={initialValue}
            handleChange={handleChangeTemp}
            index={index}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            editorKey={`${editorKey}`.substring(2)}
            readOnly={!isCoach && isCoachField}
            emojiDisabled={id === "nickname"}
          />
        )}
      </Sheet>
    </div>
  );
}

export default memo(TextareaInput);
